import React from 'react';
import { useFunctionRef } from '../Hooks/useFunctionRef';
import { useParams, useNavigate, useLocation } from 'react-router';
import { 
    ChevronLeft as BackIcon,
    ChevronRight as NextIcon,
    X as ExitIcon,
    FloppyFill
} from '../Components/Generic/Icons';

import './Journey.scss';
import { Link } from '../Components/Generic/Link';
import RoundedIcon from '../Components/Generic/RoundedIcon';
import { addClassNames } from '../Functions/Helper/Component';
import TouchIndicator from '../Components/Generic/TouchIndicator';
import withInsight from '../Hooks/withApplicationInsight';
import { deferCall } from '../Functions/Helper/Async';
import LoadingScreen from '../Components/Generic/LoadingSpinner';
import { ErrorMessage } from '../Components/ErrorMessage';
import useActionDialog from '../Provider/ActionDialogContext';

function JourneyStepsIndicator({ stepNames, stepAliases, getStepPath, currentStep, nextCallback, allHighlighted }) {
    const navigate = useNavigate();
    const JourneyMobileSteps = [];
    const JourneyProgressSteps = [];
    let currentJourneyStep;
    const getLinkCLickOverride = React.useCallback((to) => (e) => {
        e.preventDefault();
        if (nextCallback) nextCallback().then((result) => {
            if (result) navigate(to);
        }); else navigate(to);

    }, [navigate, nextCallback]);
    stepNames.forEach((stepName, index) => {
        if (!stepName) return;
        if (index) {
            JourneyProgressSteps.push(
                <div key={`${index}-divider`} className="journey-step-divider"></div>
            );
        }

        const JourneyDisplay = (
            <div key={index} className={`journey-step-indicator ${allHighlighted || index <= currentStep ? 'completed' : ''}`}>
                <div className="journey-step-index">{index + 1}</div>
                <div className="journey-step-name">{stepName}</div>
            </div>
        );
        const stepTo = getStepPath(stepAliases[index]);
        const JourneyStep = (className) => (
            <Link key={`${index}-step`} className={className} to={stepTo} onClick={getLinkCLickOverride(stepTo)}>
                {JourneyDisplay}
            </Link>
        );
        if ((!currentStep && !index) || currentStep === index) currentJourneyStep = JourneyDisplay; 
        JourneyMobileSteps.push(JourneyStep("dropdown-item unstyled w-100"));
        JourneyProgressSteps.push(JourneyStep("unstyled"));
    });

    return (
        <>
            <div className="journey-steps px-5 my-2 mb-4" >{JourneyProgressSteps}</div>
            <div className='journey-steps mobile d-flex flex-nowrap justify-content-center align-items-center mb-1'>
                <div className="dropdown">
                    <button
                        type="button"
                        className="btn btn-transparent dropdown-toggle d-flex flex-nowrap align-items-center gap-2 mb-2" 
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        {currentJourneyStep}
                    </button>
                    <div className="dropdown-menu dropdown-menu-center" >
                        {JourneyMobileSteps}
                    </div>
                </div>
            </div>
        </>
    );
}

function JourneyStep({
    journeyTitle,
    stepNames,
    currentStep,
    setCurrentStep,
    finalRoute,
    progress,
    StepComponent: SC,
    stepAliases,
    ...props
}) {
    const StepComponent = React.useMemo(() => withInsight(SC), [SC]);
    const { confirm } = useActionDialog();
    const [onNext, _nextCallback] = useFunctionRef(true);
    const [onBack, backCallback] = useFunctionRef(true);
    const [lastSave, setLastSave] = React.useState(Date.now());
    const [error, setError] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const navigate = useNavigate();
    const {id, getStepPath, returnUrl} = useCurrentNavigation();
    const nextCallback = React.useCallback(async () => {
        if (_nextCallback) {
            const result = await _nextCallback();
            if (result) setLastSave(Date.now());
            return result;
        }
        return true;
    }, [_nextCallback]);

    const navigateOut = React.useCallback(async() => {
        if (typeof window !== "undefined") {
            const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
            navigate(firstSegment ? `/${firstSegment}` : '/');
        } else {
            navigate('/');
        }
    }, [navigate]);

    const _finalRoute = React.useMemo(() => {
        if (!finalRoute) return null;
        else if (typeof finalRoute === "function") return finalRoute(id);
        else return finalRoute;
    }, [finalRoute, id]);
    const next = React.useCallback(async (triggerCallback = true) => {
        setLoading(true);
        try {
            if (triggerCallback && nextCallback && !await nextCallback()) {
                setTimeout(() => setLoading(false),150);
                return ;
            }
            setError(null);
            if (currentStep + 1 < stepNames.length) {
                setCurrentStep(currentStep + 1);
                setTimeout(() => setLoading(false),150);
            } else if (_finalRoute) {
                navigate(_finalRoute);
            } else if (returnUrl) {
                navigate(decodeURIComponent(returnUrl));
            } else navigateOut();
        } catch (error) {
            setLoading(false);
            setError(error);
        }
    }, [
        nextCallback,
        navigate,
        currentStep,
        setCurrentStep,
        _finalRoute,
        stepNames.length,
        returnUrl,
        navigateOut
    ]);

    const back = React.useCallback(async (triggerCallback = true) => {
        if (triggerCallback) {
            if(backCallback && !await backCallback()) return;
            if(currentStep !== 0 && nextCallback && !await nextCallback()) return;
        }
        if (currentStep === 0) {
            if (await confirm("Are you sure you want to exit? Unsaved changes will be lost.")) {
                if (returnUrl) {
                    navigate(returnUrl);
                } else if (_finalRoute) {
                    navigate(_finalRoute);
                } else navigateOut();
            }
        } else if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } 
    }, [
        backCallback,
        nextCallback,
        currentStep,
        setCurrentStep,
        confirm,
        navigate,
        _finalRoute,
        returnUrl,
        navigateOut
    ]);
    const stepProps = { ...props, onNext, next, onBack, back };
    let exitLink = React.useMemo(() => {
        if (returnUrl !== "forced")
            return (
                <Link to={returnUrl || "/"} onClick={(e) => {
                    e.preventDefault();
                    confirm("Are you sure you want to exit? Unsaved changes will be lost.").then((result) => {
                        if (result) {
                            navigate(returnUrl || "/");
                        }
                    });
                }} className="text-secondary position-absolute px-3 l-0">
                    <RoundedIcon 
                        className="border border-secondary"
                        Icon={ExitIcon}
                        backgroundColor="var(--mo-light)"
                        color="var(--mo-dark)"
                    />
                </Link>
            );
        else return <></>;
    }, [returnUrl,confirm, navigate]);

    const MessageComponent = React.useCallback(() => {
        if(error) return (    
            <ErrorMessage
                className={"m-0 mx-3 flex-grow-1 text-center"}
                error={error}
                action={`submitting ${stepNames[currentStep]} step`}
            />
        );

        return (
            <div className="m-0 mx-3 flex-grow-1 text-center">
                <div ref={(ref) => {;
                    if (!ref) return;
                    const nextSave = lastSave + 30000;
                    if (nextSave <= Date.now()) {
                        ref.classList.add("alert-danger");
                        ref.classList.remove("alert-info");
                    } else {
                        ref.classList.add("alert-info");
                        ref.classList.remove("alert-danger");
                        setTimeout(() => {
                            ref.classList.add("alert-danger");
                            ref.classList.remove("alert-info");
                        }, (nextSave - Date.now()));
                    }
                }} className="alert alert-info m-0 py-1" role="status">
                    <i><small>Ensure drafted changes are saved regularly.</small></i>
                </div>
            </div>
        );
    }, [error, currentStep, stepNames, lastSave]);

    return (
        <div className="journey-step px-1 position-relative">
            {StepComponent.SideComponent && (
                <div className='position-absolute r-3 t-3'><TouchIndicator style={{ fontSize: "12px"}}/></div>
            )}
            <div className='journey-header bg-white pt-3'>
                <div className="journey-title px-3 mb-3">{exitLink}<h2 className="h4">{journeyTitle}</h2></div>
                <JourneyStepsIndicator
                    stepNames={stepNames}
                    stepAliases={stepAliases}
                    currentStep={currentStep}
                    getStepPath={getStepPath}
                    nextCallback={nextCallback}
                    allHighlighted={returnUrl !== "forced"}
                />
                {(!returnUrl || returnUrl === "forced")? (

                    <div className={addClassNames("journey-progress px-4", (!stepAliases?.length || stepAliases.length <= 1) && "d-none" )}>
                        <div className="journey-progress-label mb-1"  style={{ width: `${progress}%` }}>
                            <span className="text-primary">{progress}%</span> Completed
                        </div>
                        <div className="journey-progress-bar">
                            <div className="journey-progress-bar-fill" style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                ): <div className="journey-progress px-4"><div className="journey-progress-bar">
                <div className="journey-progress-bar-fill" style={{ width: `100%` }} />
            </div></div>} 
                
            </div>

            <div className="journey-body px-4"><StepComponent {...stepProps}/></div>
            <div className="journey-actions py-3 px-4">
                {StepComponent.Actions? <StepComponent.ActionButtons {...{next, back}}/> : (
                    <React.Fragment>
                        <button 
                            type="button"
                            className="journey-action btn btn-outline-secondary rounded-pill"
                            onClick={back}
                        ><span className="me-2"><BackIcon/></span> Back</button>
                        
                        <MessageComponent/>
                        <div className="btn-group">
                            <button 
                                type="button"
                                className="btn btn-primary journey-action pe-3"
                                onClick={() => {
                                    if (nextCallback) nextCallback();
                                }}
                                ref={(ref) => {;
                                    if (!ref) return;
                                    const nextSave = lastSave + 30000;
                                    if (nextSave <= Date.now()) {
                                        ref.classList.add("anim-pulse");
                                    } else {
                                        ref.classList.remove("anim-pulse");
                                        setTimeout(() => {
                                            ref.classList.add("anim-pulse");
                                        }, (nextSave - Date.now()));
                                    }
                                }} 
                            ><span className="me-2"><FloppyFill/></span> Save</button>
                            <button 
                                type="button"
                                className="btn btn-secondary journey-action"
                                onClick={() => next()}
                            >Next <span className="ms-2"><NextIcon/></span></button>
                        </div>
                    </React.Fragment>
                )}
                
            </div>
            {loading && <LoadingScreen loading={loading} className="position-absolute" />}
        </div>
    );
}

function JourneySidePanel({SideComponent: SC, ...props}) {
    const SideComponent = React.useMemo(() => SC? withInsight(SC) : SC, [SC]);
    if (!SC) return <div className='journey-side-panel empty'/>;
    return (
        <div className="journey-side-panel" ><SideComponent {...props}/></div>
    ); 
}

function getProgress(totalStepWeight, StepComponents, currentStep, currentStepProgress = 0) {
    let completedStepWeight = 0;
    for (let i = 0; i < currentStep; i++) {
        completedStepWeight += StepComponents[i].stepWeight || 1;
    }
    completedStepWeight += (StepComponents[currentStep].stepWeight || 1) * (currentStepProgress || 0);
    return Math.round((completedStepWeight / totalStepWeight) * 100);

}
function useCurrentNavigation() {
    const { pathname, hash, search } = useLocation();
    const { id, step } = useParams();
    const returnUrl = React.useMemo(() => {
        let returnUrl;
        search.substring(1).split("&").forEach(param => {   
            const [key, value] = param.split("=");
            if (key === "returnUrl") returnUrl = value;
        });
        if (returnUrl) {
            return decodeURIComponent(returnUrl);
        } else {
            return undefined;
        }
    }, [search]);
    const getStepPath = React.useCallback((newStep) => {
        const pathnames = pathname.split("/").filter(a => a);
        if (id) {
            pathnames[pathnames.length - 2] = newStep;
            pathnames[pathnames.length - 1] = id;
        } else if (step) {
            pathnames[pathnames.length - 1] = newStep;
        } else {
            pathnames.push(newStep);
        }
        return `/${pathnames.join("/")}${hash}${search}`;
    }, [pathname, id, step, hash, search]);
    return {step, getStepPath, returnUrl, id};
}

function useSteppedRoute(stepAliases) {
    const navigate = useNavigate();
    const {step, getStepPath} = useCurrentNavigation();
    
    let currentStep = React.useMemo(() => {
        let currentStep = -1;
        if (step) {
            if (isNaN(step)) {
                currentStep = stepAliases.indexOf(step);
            } else {
                currentStep = parseInt(step);
            }
        }
        if (currentStep < 0 || (currentStep >= stepAliases.length && stepAliases.length > 0)) {
            if (currentStep < 0) {
                currentStep = 0;   
            } else {
                currentStep = stepAliases.length - 1;
            }
            deferCall(() => navigate(getStepPath(stepAliases[currentStep])));
            
        }
        return currentStep;
    }, [step, stepAliases, navigate, getStepPath]);

    const setCurrentStep = React.useCallback((newStep) => {
        if (isNaN(step)) {
            newStep = stepAliases[newStep];
            if (newStep) navigate(getStepPath(newStep));
        }
    }, [step, stepAliases, navigate, getStepPath]);

    return [currentStep, setCurrentStep];
}

function CurrentJourneyStep({ 
    stepNames, stepAliases, StepComponents, totalStepWeight, ..._props 
}) {
    const [currentStep, setCurrentStep] = useSteppedRoute(stepAliases);
    const [currentStepProgress, setCurrentStepProgress] = React.useState(0);
    const StepComponent = StepComponents[currentStep];
    const stepName = stepNames[currentStep];

    React.useEffect(() => {
        setCurrentStepProgress(0);
    }, [currentStep]);

    const props = { 
        ..._props,
        
        currentStep,
        setCurrentStep,        
        stepNames,
        stepName,
        stepAliases,

        progress: getProgress(
            totalStepWeight,
            StepComponents,
            currentStep,
            currentStepProgress
        ),
        setCurrentStepProgress
    };
    const journeyContainer = React.useRef();

    return (
        <div ref={journeyContainer} className="journey-wrapper">
            <div className={addClassNames("journey-container", StepComponent.SideComponent && " with-side-panel")}>
            <JourneyStep StepComponent={StepComponent} {...props}/>
            <JourneySidePanel
                journeyContainer={journeyContainer}
                SideComponent={StepComponent.SideComponent}
                {...props}
            />
            </div>
        </div>
    )
    
}

export default function Journey({journeyTitle = "Complete the Steps", Provider, finalRoute}, ...StepComponents) {
    // if (StepComponents?.length <= 1) throw new Error('please provide more than one step for journey');
    const stepNames = StepComponents.map(step => step.stepName || step.name);
    const stepAliases = StepComponents.map((step, idx) => 
        step.stepAlias || stepNames[idx].replace(/\s/g, '-').toLowerCase()
    );
    const totalStepWeight = StepComponents.reduce((total, step) => total + (step.stepWeight || 1), 0);

    return function (_props) {
        const props = {
            stepNames,
            stepAliases,
            totalStepWeight,
            journeyTitle,
            finalRoute,
            StepComponents,
            ..._props
        };
        if (Provider) return <Provider><CurrentJourneyStep {...props}/></Provider>;
        else return <CurrentJourneyStep {...props}/>;
    };
}